import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { apiClient } from '../services/apiClient';

/**
 * MikrotikGatewaySelector — dropdown pilih MikroTik + inline edit hotspot_gateway_ip.
 *
 * Digunakan di modal print voucher untuk menentukan IP gateway hotspot yang akan
 * di-encode ke URL QR. Beda cabang / MikroTik = beda gateway IP.
 *
 * Props:
 *   - value: { id, hotspot_gateway_ip, name } | null
 *   - onChange: (selected) => void
 */
const MikrotikGatewaySelector = ({ value, onChange }) => {
  const [mikrotiks, setMikrotiks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editIp, setEditIp] = useState('');
  const [loading, setLoading] = useState(true);

  const selected = mikrotiks.find((m) => m.id === value?.id);

  useEffect(() => {
    let mounted = true;
    apiClient
      .get('/api/mikrotiks')
      .then((res) => {
        if (!mounted) return;
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        setMikrotiks(list);
        // Auto-select MikroTik pertama yang punya hotspot_gateway_ip
        if (!value?.id) {
          const withIp = list.find((m) => m.hotspot_gateway_ip);
          if (withIp) {
            setEditIp(withIp.hotspot_gateway_ip || '');
            onChange({
              id: withIp.id,
              hotspot_gateway_ip: withIp.hotspot_gateway_ip,
              name: withIp.name,
            });
          }
        } else {
          const match = list.find((m) => m.id === value.id);
          if (match) setEditIp(match.hotspot_gateway_ip || '');
        }
      })
      .catch((e) => {
        console.error('Failed fetching mikrotiks', e);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
    // (deps kosong: initial fetch only)
  }, []);

  const handleMikrotikChange = (id) => {
    const m = mikrotiks.find((mk) => mk.id === id);
    if (!m) {
      onChange(null);
      return;
    }
    setEditIp(m.hotspot_gateway_ip || '');
    onChange({
      id: m.id,
      hotspot_gateway_ip: m.hotspot_gateway_ip,
      name: m.name,
    });
  };

  const handleSaveIp = async () => {
    if (!selected) return;
    const trimmed = String(editIp || '').trim();
    // IP validation regex (allows optional port)
    if (trimmed && !/^(\d{1,3}\.){3}\d{1,3}(:\d+)?$/.test(trimmed)) {
      toast.error(`Format IP tidak valid: '${trimmed}'. Contoh: 10.5.50.1`);
      return;
    }
    setSaving(true);
    try {
      await apiClient.patch(
        `/api/mikrotiks/${selected.id}/hotspot-gateway-ip`,
        { hotspot_gateway_ip: trimmed || null }
      );
      toast.success('Gateway IP tersimpan');
      // Update local list
      setMikrotiks((prev) =>
        prev.map((m) =>
          m.id === selected.id ? { ...m, hotspot_gateway_ip: trimmed || null } : m
        )
      );
      onChange({
        id: selected.id,
        hotspot_gateway_ip: trimmed || null,
        name: selected.name,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal simpan gateway IP');
    }
    setSaving(false);
  };

  const dirty = selected && editIp !== (selected.hotspot_gateway_ip || '');

  return (
    <div
      className='border-2 border-purple-200 dark:border-purple-800 rounded-lg p-3 mb-3 bg-purple-50 dark:bg-purple-900/20'
      data-testid='mikrotik-gateway-selector'
    >
      <label className='text-xs font-semibold text-purple-900 dark:text-purple-100 mb-1 block'>
        🌐 MikroTik Sumber Voucher (untuk QR Auto-Login)
      </label>
      <select
        value={selected?.id || ''}
        onChange={(e) => handleMikrotikChange(e.target.value)}
        className='w-full h-8 text-xs border rounded px-2 mb-2 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100'
        data-testid='print-mikrotik-selector'
        disabled={loading}
      >
        <option value=''>{loading ? 'Loading...' : '-- Pilih MikroTik --'}</option>
        {mikrotiks.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name} {m.hotspot_gateway_ip ? `— ${m.hotspot_gateway_ip}` : '— (belum ada gateway IP)'}
          </option>
        ))}
      </select>
      {selected && (
        <div className='flex gap-2 items-center'>
          <input
            type='text'
            value={editIp}
            onChange={(e) => setEditIp(e.target.value)}
            placeholder='10.5.50.1'
            className='flex-1 h-7 text-xs border rounded px-2 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100'
            data-testid='print-mikrotik-gateway-ip-input'
          />
          <Button
            onClick={handleSaveIp}
            disabled={saving || !dirty}
            size='sm'
            className='h-7 text-xs bg-purple-600 hover:bg-purple-700'
            data-testid='print-mikrotik-gateway-ip-save'
          >
            {saving ? 'Simpan...' : 'Simpan IP'}
          </Button>
        </div>
      )}
      <p className='text-[10px] text-purple-700 dark:text-purple-300 mt-1'>
        QR akan encode:{' '}
        <code>
          http://{selected?.hotspot_gateway_ip || editIp || 'GATEWAY_IP'}:80/login?username=XXX
        </code>
      </p>
    </div>
  );
};

export default MikrotikGatewaySelector;
